// Cart page progressive enhancement. Same "swap the fresh Section Rendering API
// markup into a data-* anchor" pattern as the cart-drawer's <cart-drawer> element
// in assets/global.js (not edited here — this is its own small module for the
// full cart page, which also needs its own section re-rendered, not just the drawer).
//
// Without this script the page still works: quantity inputs post through
// `updates[]` + the "Update cart" button, and "Remove" is a plain link to
// `url_to_remove`. This file only makes those interactions instant.

const S = window.Spotter || {};
const MAIN_SECTION = 'main-cart';
const DRAWER_SECTION = 'cart-drawer';

class CartPageForm extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-enhanced');
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
  }

  onClick(e) {
    const link = e.target.closest('[data-line-remove]');
    if (!link) return;
    e.preventDefault();
    this.changeLine(link.dataset.line, 0, link);
  }

  onChange(e) {
    if (!e.target.matches('[data-qty]')) return;
    const line = e.target.closest('[data-line]')?.dataset.line;
    if (line) this.changeLine(line, Number(e.target.value), e.target);
  }

  async changeLine(line, quantity, trigger) {
    const anchor = this.querySelector('[data-main-cart]');
    anchor?.setAttribute('aria-busy', 'true');
    this.hideError();
    try {
      const res = await fetch((S.routes?.cartChange || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity,
          sections: [MAIN_SECTION, DRAWER_SECTION],
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || S.strings?.cartError || 'Something went wrong.');
      }
      this.render(data.sections, trigger);
    } catch (err) {
      this.showError(err.message);
      anchor?.removeAttribute('aria-busy');
    }
  }

  render(sections, trigger) {
    const mainHtml = sections && sections[MAIN_SECTION];
    if (mainHtml) {
      const doc = new DOMParser().parseFromString(mainHtml, 'text/html');
      const fresh = doc.querySelector('[data-main-cart]');
      const current = this.querySelector('[data-main-cart]');
      if (fresh && current) {
        current.replaceWith(fresh);
        // Keep keyboard focus near where the shopper was working.
        if (trigger?.dataset?.line) {
          this.querySelector(`[data-line-remove][data-line="${trigger.dataset.line}"]`)?.focus()
            || this.querySelector('[data-qty]')?.focus();
        }
      }
    }

    const drawerHtml = sections && sections[DRAWER_SECTION];
    if (drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = document.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      const count = fresh?.dataset.itemCount;
      if (count !== undefined) {
        document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: Number(count) } }));
      }
    }
  }

  showError(message) {
    const el = this.querySelector('[data-cart-error]');
    if (el) { el.textContent = message; el.hidden = false; }
  }

  hideError() {
    const el = this.querySelector('[data-cart-error]');
    if (el) el.hidden = true;
  }
}
customElements.define('cart-page-form', CartPageForm);
